<template>
  <div>
    <div
      v-if="!isReadOnly"
      class="d-flex justify-content-end align-items-center flex-wrap goal-top-actions__container"
    >
      <!-- AG-05 -- "Meta con Asistente IA" (primera meta / Sub Área nueva): a diferencia del
           mismo disparador dentro de SubAreaNecessity.vue, aquí Sub Área y Fortaleza/Necesidad
           todavía NO están fijas -- el especialista debe elegirlas en el GoalForm de abajo
           (showClassifier por defecto), que ahora es un formulario de 2 pasos (ver
           GoalForm.vue): paso 1 solo esos dos campos + "Continuar", paso 2 recién ahí el resto.
           Si al hacer click todavía faltan, el click solo abre el paso 1 y queda pendiente
           (handleAiGoalTriggerClick) -- el agente se convoca al confirmar el paso 1
           (handleClassifierConfirmed), nunca antes, así que nunca se ve el formulario manual
           completo ("Crear Meta" incluido) sin que la clasificación ya esté decidida. Versión
           compacta (Variante B del mockup aprobado por el usuario): solo ícono en reposo, se
           expande con un fade al hacer hover/focus mostrando el texto completo -- ver el
           detalle técnico en el comentario de .ai-goal-trigger más abajo. -->
      <button
        v-if="aiAssistantEnabled"
        type="button"
        class="ai-goal-trigger mr-2"
        title="Meta con Asistente IA"
        @click="handleAiGoalTriggerClick($event)"
      >
        <span class="ai-trigger-icon">
          <AiSparkIcon :size="16" variant="white" />
        </span>
        <span class="ai-trigger-label">Meta con Asistente IA</span>
      </button>
      <ActionButton
        i="fas fa-file-plus"
        class="mr-2"
        variant="success"
        @click="addNewGoal"
      >
        Añadir Meta
      </ActionButton>
    </div>

    <SectionTitle :has-line="true" :content="showObjetivesSection ? 'Metas y Objetivos' : 'Metas'" />

    <Error
      v-if="
        subAreaOptions.length > 0 && selectedSubAreas.length <= 0 && !isReadOnly
      "
      message="Para documentar la meta es requerido primero realizar la selección de sub-áreas."
    />

    <Error
      v-if="hasGoalsError && !isReadOnly"
      :message="`Favor de agregar las metas${showObjetivesSection ? ' y objetivos': ''}${minRequiredGoals > 1 ? ` (debe contar con un mínimo de ${minRequiredGoals} Metas)`: ''}.`"
    />

    <Error
      v-if="showIncompleteNecessitySelectionError && !isReadOnly"
      message="Favor relacionar todas las necesidades a las metas."
    />

    <Error
      v-if="isIncompleteSubAreaSelection && !isReadOnly"
      message="Favor relacionar todas las sub áreas a las metas."
    />

    <div v-if="showNewEmptyGoal" :class="{ 'goal-new-form__container': formOpenedViaAi }">
      <GoalForm
        ref="newEmptyGoalForm"
        v-model="newGoal"
        :sub-area-options="selectedSubAreas"
        :necessity-options="filteredStrengthAndNecessityOptions"
        :term-options="termOptions"
        :suitability-options="suitabilityOptions"
        :can-cancel="!!goals.length"
        :skills-required="skillsRequired"
        :adequacy-options="adequacyOptions"
        :show-adequacy="showAdequacy"
        :suppress-manual-fields="aiGoalModal.visible"
        :two-step-gate="formOpenedViaAi"
        @save="saveGoalHandler"
        @cancel="handleCancelNewGoal"
        @classifier-confirmed="handleClassifierConfirmed"
      />
    </div>
    
    <div
      class="goal-results__container"
      v-for="(subAreaGroup, index) in goalsGroupedBySubArea"
      :key="subAreaGroup.subAreaId"
    >
      <SubAreaContainer
        :custom-id="`sub-area-${index}`"
        :ref="`sub-area-${index}`"
        :value="subAreaGroup"
        :hasError="hasError"
        :term-options="termOptions"
        :adequacy-options="adequacyOptions"
        :instrument-options="instrumentOptions"
        :suitability-options="suitabilityOptions"
        :necessity-options="necessityOptions"
        :skills-required="skillsRequired"
        :is-read-only="isReadOnly"
        :show-objetives-section="showObjetivesSection"
        :ai-assistant-enabled="aiAssistantEnabled"
        :auto-open-ai-objectives-goal-id="pendingAiChainGoalId"
        :suppress-auto-open-objetive-form="aiBulkSelectInProgress"
        @pending-goal="setPendingSubAreaGoal"
        @pending-objetive="setPendingSubAreaObjetive"
        @add-sub-area-goal="relayCreateGoal"
        @add-objetive="relayAddObjetive"
        @delete-goal="$emit('delete-goal', $event)"
        @update-goal="$emit('update-goal', $event)"
        @update-objetive="$emit('update-objetive', $event)"
        @delete-objetive="$emit('delete-objetive', $event)"
        @delete-goal-necessity="$emit('delete-goal-necessity', $event)"
        @update-skills="$emit('update-skills', $event)"
        @ai-chain-goal-created="handleAiChainGoalCreated"
        @clear-ai-chain="pendingAiChainGoalId = null"
      />
    </div>

    <!-- AG-05 -- "Meta con Asistente IA" (primera meta / Sub Área nueva) -- ver el mismo
         patrón, ya con contexto fijo, en SubAreaNecessity.vue. -->
    <AiAssistantModal
      :visible="aiGoalModal.visible"
      :loading="aiGoalModal.loading"
      :error="aiGoalModal.error"
      :anchor-rect="aiGoalModal.anchorRect"
      :best-practices="aiGoalModal.bestPractices"
      :can-refine="!!aiGoalModal.suggestions.length"
      refine-placeholder="Ej: prioriza destrezas de lectura, término más corto..."
      :can-give-feedback="!!(aiGoalModal.suggestions.length && aiGoalModal.aiGenerationLogId)"
      :feedback="aiGoalModal.feedback"
      :feedback-submitting="aiGoalModal.feedbackSubmitting"
      title="Metas Sugeridas por IA"
      :loading-text="aiGoalModal.loadingText"
      @close="closeAiGoalModal"
      @refine="handleRefineGoal"
      @feedback="handleFeedbackGoal"
    >
      <!-- contexto: a qué Sub Área/Necesidad corresponden las Metas sugeridas
           (mockup de Figma) -- Sub Área y Fortaleza/Necesidad las elige el especialista en el
           GoalForm de abajo ANTES de convocar al agente (ver handleAiGoalTriggerClick), así que
           ya están disponibles en newGoal. -->
      <div
        v-if="newGoal.subArea || newGoal.strenghtsAndNecessities.length"
        class="ai-goal-context__breadcrumb"
      >
        <span v-if="newGoal.subArea">Sub Área: <strong>{{ newGoal.subArea.name }}</strong></span>
        <span v-if="newGoal.strenghtsAndNecessities.length">
          Necesidad: <strong>{{ aiGoalNecessityText }}</strong>
        </span>
      </div>

      <div v-if="!aiGoalModal.suggestions.length" class="ai-modal-empty">
        No se generaron metas sugeridas para esta necesidad. Intente nuevamente.
      </div>

      <div
        v-for="(suggestion, idx) in aiGoalModal.suggestions"
        :key="idx"
        class="ai-goal-suggestion"
      >
        <div class="ai-goal-suggestion__header">
          <div class="ai-goal-suggestion__header-left">
            <span class="ai-goal-suggestion__badge">
              <AiSparkIcon :size="13" variant="white" /> Meta #{{ idx + 1 }}
            </span>
            <!-- "editar antes de usar": señal solo visual de que el texto ya no es el
                 que propuso la IA tal cual -- la auditoría real (qué corrida quedó vinculada a la
                 Meta) la decide applyAiGoalSuggestion, no esta etiqueta. Ver aiSuggestionEditMixin.js. -->
            <span v-if="aiEditWasEdited(suggestion, formatAiGoalEditableText)" class="ai-goal-suggestion__edited-tag">
              <i class="fa fa-pencil" /> Editado
            </span>
          </div>
          <!-- botón único (ícono) para Editar/Cancelar, mockup aprobado por el
               usuario -- reemplaza el par de botones de texto que vivía en
               .ai-goal-suggestion__actions (ver más abajo, ahora con un solo botón). Íconos de
               FontAwesome, ya usados en el resto de la app, en vez de componentes SVG propios. -->
          <button
            type="button"
            class="ai-btn-icon"
            :class="{ 'ai-btn-icon--active': aiEditIsActive(suggestion) }"
            :title="aiEditIsActive(suggestion) ? 'Cancelar edición' : 'Editar'"
            @click="handleAiGoalEditToggle(suggestion)"
          >
            <i v-if="aiEditIsActive(suggestion)" class="fas fa-times" />
            <i v-else class="fa fa-pencil" />
          </button>
        </div>
        <!-- la pastilla "Meta #N" queda sola arriba (ver header) para dar contexto
             primero; el checkbox flota (float: left) al lado de la 1ª línea del texto -- la
             sugerencia se lee primero, se selecciona después. Elegido sobre varios mockups de
             imagen que se le mostraron al usuario antes de tocar código. Solo las sugerencias
             COMPLETAS (con match real de Término/Adecuacidad contra catálogo, ver
             isAiGoalSuggestionComplete) son elegibles para inserción masiva -- las incompletas
             siguen requiriendo completarlas a mano vía "Usar esta meta", y por eso no muestran
             checkbox. -->
        <div class="ai-goal-suggestion__content">
          <CustomCheckbox
            v-if="isAiGoalSuggestionComplete(suggestion) && !aiEditIsActive(suggestion)"
            v-model="suggestion._selected"
            title="Seleccionar"
            aria-label="Seleccionar"
            class="ai-goal-suggestion__checkbox"
          />
          <!-- "editar antes de usar": ni Término ni Adecuacidad se editan como texto libre --
               son frases de catálogo que la UI inyecta en la oración final (el backend no tiene
               columna propia para ninguna de las dos, ver PeiProgramServiceGoalDTO), así que un
               valor escrito a mano ahí podría terminar sin corresponder a ningún valor real. Los
               dos dropdowns de abajo (mismo catálogo de siempre, termOptions/suitabilityOptions)
               dejan al especialista elegir otro Término/Adecuacidad si no está de acuerdo con el
               de la IA, pero siempre un valor real. El textarea (mismo foco violeta que ya usa
               "Refinar Resultado", ver AiAssistantModal.vue, .ai-modal-refine-input) queda
               reducido a editar SOLO la Actividad (formatAiGoalEditableText). -->
          <CustomDropDown
            v-if="aiEditIsActive(suggestion)"
            v-model="suggestion._aiEditTerm"
            class="ai-goal-suggestion__catalog-edit"
            label="Término:"
            :options="termOptions"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
          />
          <textarea
            v-if="aiEditIsActive(suggestion)"
            v-model="suggestion._aiEditText"
            class="ai-goal-suggestion__text-edit"
            rows="3"
          />
          <CustomDropDown
            v-if="aiEditIsActive(suggestion)"
            v-model="suggestion._aiEditSuitability"
            class="ai-goal-suggestion__catalog-edit"
            label="Adecuacidad:"
            :options="suitabilityOptions"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
          />
          <div v-else class="ai-goal-suggestion__text">
            {{ formatAiGoalPreview(suggestion) }}
          </div>
          <div v-if="!aiEditIsActive(suggestion)" class="ai-goal-suggestion__meta">
            <span><strong>Término:</strong> {{ aiGoalDisplayTermino(suggestion) }}</span>
            <span>
              <strong>Adecuacidad:</strong> {{ aiGoalDisplayAdecuacidad(suggestion) }}
            </span>
          </div>
          <div class="ai-goal-suggestion__actions">
            <button
              type="button"
              class="ai-btn ai-btn-primary"
              @click="handleAiGoalSelected(suggestion)"
            >
              <AiSparkIcon :size="13" variant="white" /> Usar esta meta
            </button>
          </div>
        </div>
      </div>

      <!-- inserción masiva: "Añadir seleccionadas" guarda, en cadena, todas las
           sugerencias marcadas (reutilizando el mismo guardado que "Usar esta meta" individual,
           una por una) sin abrir el encadenado de "Objetivo con Asistente IA" para cada una.
           Ronda 24 -- lógica y fila extraídas a aiBulkSelectMixin.js/AiBulkSelectBar.vue,
           reusadas también por SubAreaNecessity.vue (Metas) y SubAreaGoalCallout.vue
           (Objetivos). -->
      <AiBulkSelectBar
        :selectable-count="aiBulkSelectableSuggestions.length"
        :selected-count="aiBulkSelectedCount"
        :all-selected="aiBulkAllSelected"
        :disabled="aiGoalModal.loading"
        @toggle-select-all="aiBulkToggleSelectAll"
        @add-selected="aiBulkAddSelected"
      />

      <div class="ai-modal-actions">
        <button
          type="button"
          class="ai-btn ai-btn-outline"
          :disabled="aiGoalModal.loading"
          @click="requestAiGoal"
        >
          <i class="fas fa-rotate-left" /> Regenerar
        </button>
      </div>
    </AiAssistantModal>
  </div>
</template>

<script>
import { SectionTitle } from "@/components/pei/shared";
import store from '@/store/index';
import GoalForm from './GoalForm.vue';
import SubAreaContainer from './sub-area/SubAreaContainer.vue';
import ActionButton from "ActionButton";
import Error from '@/components/common/Error';
import { CustomCheckbox } from '@/components/common';
import CustomDropDown from '@/components/common/CustomDropDown';
import alert from '@/mixin/alert.mixin';
import AiAssistantModal from '@/components/pei/shared/ai-assistant/AiAssistantModal.vue';
import AiSparkIcon from '@/components/pei/shared/ai-assistant/AiSparkIcon.vue';
import AiBulkSelectBar from '@/components/pei/shared/ai-assistant/AiBulkSelectBar.vue';
import aiBulkSelectMixin from '@/components/pei/shared/ai-assistant/aiBulkSelectMixin.js';
import aiSuggestionEditMixin from '@/components/pei/shared/ai-assistant/aiSuggestionEditMixin.js';
import { generateSuggestion, refineSuggestion, markSuggestionUsed, submitFeedback } from '@/services/pei/aiAssistant.js';

import { modulesName } from "@/modules";
import { createNamespacedHelpers } from "vuex";
const { mapGetters, mapState } = createNamespacedHelpers(
  modulesName.programServiceModuleName
);

export default {
  name: 'GoalsManager',
  mixins: [alert, aiBulkSelectMixin, aiSuggestionEditMixin],
  components: {
    GoalForm,
    ActionButton,
    SectionTitle,
    SubAreaContainer,
    Error,
    CustomCheckbox,
    CustomDropDown,
    AiAssistantModal,
    AiSparkIcon,
    AiBulkSelectBar,
  },
  props: {
    goals: {
      type: Array,
      default: () => []
    },
    areaId: {
      type: Number,
      default: 0
    },
    subAreaOptions: {
      type: Array,
      default: () => []
    },
    necessityOptions: {
      type: Array,
      default: () => []
    },
    termOptions: {
      type: Array,
      default: () => []
    },
    adequacyOptions: {
      type: Array,
      default: () => []
    },
    suitabilityOptions: {
      type: Array,
      default: () => []
    },
    instrumentOptions: {
      type: Array,
      default: () => [],
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    minRequiredGoals: {
      type: [String, Number],
      default: 1
    },
    skillsRequired: {
      type: Boolean,
      default: true
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    showObjetivesSection: {
      type: Boolean,
      default: true
    },
    showAdequacy: {
      type: Boolean,
      default: false
    },
    // AG-05 -- pasa el sysparam EnableAiAssistantPei (resuelto en AddProgramService.vue,
    // igual que Tab3.vue en III-A/III-B) hacia SubAreaContainer -> SubAreaNecessity ->
    // SubAreaGoalCallout, que es donde vive el disparador "Objetivo con Asistente IA".
    aiAssistantEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      newGoal: {
        subArea: null,
        strenghtsAndNecessities: [],
        term: null,
        activity: '',
        studentName: store.getters.SelectedStudent.firstName,
      },
      forceShow: false,
      pendingSubAreaGoals: [],
      pendingSubAreaObjetives: [],
      // AG-05 -- "Meta + Objetivos encadenados": id real de la meta recién creada por "Meta
      // con Asistente IA" (de cualquiera de los dos disparadores, este o el de
      // SubAreaNecessity.vue) -- se recibe directo de la respuesta del guardado (ver
      // AddProgramService.handleCreateGoal, parámetro onCreated), no de un match de texto.
      // Se usa para identificar la meta correcta en `goals` tras el refetch y auto-abrir
      // "Objetivo con Asistente IA" sobre ella.
      pendingAiChainGoalId: null,
      pendingAiGoalChain: false,
      // true entre el click en "Meta con Asistente IA" sin Sub Área/Fortaleza-Necesidad
      // todavía elegidas y el "Continuar" que el especialista aprieta dentro del GoalForm
      // (paso 1 de 2, ver GoalForm.vue) una vez las completa. No se dispara solo al cambiar
      // la selección: Fortalezas/Necesidades es de selección MÚLTIPLE, así que no hay forma
      // de saber cuándo terminó de elegir sin que lo confirme -- por eso el disparo real
      // vive en handleClassifierConfirmed, no en un watcher.
      pendingAiGoalTrigger: false,
      pendingAiGoalAnchorRect: null,
      // true mientras el formulario de "Añadir Meta" está abierto porque lo disparó una
      // acción de IA (botón "Meta con Asistente IA", o el guardado en cadena de "Usar esta
      // meta"/inserción masiva) -- controla el fondo violeta suave (.goal-new-form__container)
      // en el template. Si el formulario se abrió con el botón regular "Añadir Meta", esto
      // queda en false y el formulario mantiene su estilo original (pedido explícito del
      // usuario: el resaltado es solo para el flujo de IA).
      formOpenedViaAi: false,
      aiGoalModal: {
        visible: false,
        loading: false,
        // texto del estado `loading` de arriba: antes era un string fijo en el
        // template ("Generando metas sugeridas…"), ahora vive acá porque también se usa,
        // con otro texto, mientras se guarda de verdad la meta elegida con "Usar esta meta"
        // (ver handleAiGoalSelected) -- antes ese guardado corría totalmente invisible.
        loadingText: 'Generando metas sugeridas…',
        error: '',
        anchorRect: null,
        suggestions: [],
        aiGenerationLogId: null,
        feedback: null,
        feedbackSubmitting: false,
        bestPractices: null,
      },
    };
  },
  computed: {
    ...mapState(["selectedNecessities"]),
    ...mapGetters(["selectedSubAreas", "isIncompleteNecessitySelection"]),
    showNewEmptyGoal() {
      return !this.goals.length || this.forceShow;
    },
    // texto de contexto para el modal de "Metas Sugeridas por IA" -- mismo patrón
    // que `necessityText` en goalsGroupedBySubArea (toma el primer nombre no vacío), pero acá
    // sobre newGoal.strenghtsAndNecessities porque todavía no hay Meta guardada.
    aiGoalNecessityText() {
      return this.newGoal.strenghtsAndNecessities.find(x => !!x.name)?.name ?? '';
    },
    // adaptadores requeridos por aiBulkSelectMixin.js (ver el contrato
    // documentado ahí): exponen el modal/las sugerencias de ESTE componente bajo los nombres
    // fijos que el mixin espera, sin que el mixin necesite conocer la forma de `aiGoalModal`.
    aiBulkSuggestions() {
      return this.aiGoalModal.suggestions;
    },
    aiBulkModalLoading: {
      get() { return this.aiGoalModal.loading; },
      set(value) { this.aiGoalModal.loading = value; },
    },
    aiBulkModalLoadingText: {
      get() { return this.aiGoalModal.loadingText; },
      set(value) { this.aiGoalModal.loadingText = value; },
    },
    aiBulkModalVisible: {
      get() { return this.aiGoalModal.visible; },
      set(value) { this.aiGoalModal.visible = value; },
    },
    aiBulkItemNoun() {
      return 'meta';
    },
    filteredStrengthAndNecessityOptions() {
      const subAreaFilter = this.newGoal.subArea?.id;
      const notAvailableStrenghtsAndNecessities = this.goals
        .filter(x => x.areaId == this.areaId && x.subAreaId == subAreaFilter)
        .map(
          goal => goal.strenghtsAndNecessities
            .map(sn => ({ isStrength: sn.isStrength, id: sn.id}))
        )
        .flat();

      return this.necessityOptions.filter(x =>
          !notAvailableStrenghtsAndNecessities.find(
            y => y.id == x.id
            && y.isStrength == x.isStrength)
      );
    },
    goalsGroupedBySubArea() {
      const subAreaIds = new Set(this.goals.map(x => x.subAreaId));
      const groups = [];

      subAreaIds.forEach(subAreaId => {
        groups.push({
          subAreaId: subAreaId,
          subAreaText: this.goals.find(x => x.subAreaId == subAreaId)?.subAreaDesc,
          subAreaGoals: this.goals
            .filter(x => x.subAreaId == subAreaId)
            .map(x => ({
              ...x,
              necessityText: x.strenghtsAndNecessities.find(x => !!x.name)?.name ?? 'N/A',
            }))
        });
      });

      return groups;
    },
    hasGoalsError() {
      const currentCount = this.goals?.length ?? 0;
      return this.hasError && currentCount < this.minRequiredGoals;
    },
    showIncompleteNecessitySelectionError() {
      return !this.hasGoalsError && this.hasError && this.isIncompleteNecessitySelection;
    },
    isIncompleteSubAreaSelection() {
      const usedSubAreaIds = this.goals.map(x => x.subAreaId);
      const isIncomplete = this.selectedSubAreas.some(x => !usedSubAreaIds.includes(x.id));

      return !this.hasGoalsError && this.hasError && isIncomplete;
    },
    havePendingNewGoal() {
      return this.showNewEmptyGoal && (
        !!this.newGoal.subArea ||
        !!this.newGoal.strenghtsAndNecessities.length ||
        !!this.newGoal.term ||
        !!this.newGoal.activity
      );
    },
    haveGoalPendingChanges() {
      return this.havePendingNewGoal || this.pendingSubAreaGoals.length > 0;
    },
    havePendingChanges() {
      return this.haveGoalPendingChanges || this.pendingSubAreaObjetives.length > 0;
    }
  },
  methods: {
    setPendingSubAreaObjetive(data) {
      this.pendingSubAreaObjetives = this.pendingSubAreaObjetives.filter(x => x != data.customId);
      
      if (!!data.pending) {
        this.pendingSubAreaObjetives.push(data.customId);
      }
    },
    setPendingSubAreaGoal(data) {
      this.pendingSubAreaGoals = this.pendingSubAreaGoals.filter(x => x != data.customId);
      
      if (!!data.pending) {
        this.pendingSubAreaGoals.push(data.customId);
      }
    },
    async save() {
      let isValid = true;

      if(this.havePendingNewGoal) {
        isValid = isValid && await this.$refs.newEmptyGoalForm.save();
      }

      if(!!this.pendingSubAreaGoals.length) {
        for (let index = 0; index < this.pendingSubAreaGoals.length; index++) {
          isValid = isValid && await this.$refs[this.pendingSubAreaGoals[index]][0].savePendingGoals();
        }
      }
      
      if(!!this.pendingSubAreaObjetives.length) {
        for (let index = 0; index < this.pendingSubAreaObjetives.length; index++) {
          isValid = isValid && await this.$refs[this.pendingSubAreaObjetives[index]][0].savePendingObjetives();
        }
      }

      return isValid;
    },
    resetState() {
      this.newGoal = {
        subArea: null,
        strenghtsAndNecessities: [],
        term: null,
        activity: '',
        studentName: store.getters.SelectedStudent.firstName,
      };
      this.forceShow = false;
      this.pendingAiGoalTrigger = false;
      this.pendingAiGoalAnchorRect = null;
      this.formOpenedViaAi = false;
    },
    addNewGoal() {
      this.forceShow = true;
      this.formOpenedViaAi = false;
    },
    handleCancelNewGoal() {
      this.forceShow = false;
      this.pendingAiGoalTrigger = false;
      this.pendingAiGoalAnchorRect = null;
      this.formOpenedViaAi = false;
    },
    // AG-05 -- "Meta + Objetivos encadenados": cuando el guardado viene de "Meta con
    // Asistente IA" (pendingAiGoalChain), pasamos un callback `onCreated` en el emit --
    // AddProgramService.handleCreateGoal lo invoca con la Meta real ya guardada (con su
    // peiProgramServiceGoalId) DESPUÉS de que el refetch de `goals` ya terminó, así que para
    // cuando esto corre la Meta nueva ya está en la lista y SubAreaGoalCallout puede
    // identificarla por id. Un guardado manual normal no pasa callback -- comportamiento
    // idéntico a como estaba.
    async saveGoalHandler(goal) {
      if (this.pendingAiGoalChain) {
        this.pendingAiGoalChain = false;
        this.$emit('create-goal', goal, (created) => {
          // en inserción masiva (aiBulkSelectInProgress, ver aiBulkSelectMixin.js) NO
          // se dispara el encadenado de "Objetivo con Asistente IA" por cada Meta insertada --
          // el especialista lo abre a mano para la que quiera, una vez cerrado el modal de
          // Metas (ver AskUserQuestion: "No encadenar").
          if (!this.aiBulkSelectInProgress) {
            this.pendingAiChainGoalId = created?.peiProgramServiceGoalId ?? null;
          }
          // avisa a handleAiGoalSelected (si está esperando) que el guardado real
          // (POST + refetch, ver AddProgramService.handleCreateGoal) ya terminó, para que
          // pueda apagar el loading del modal de IA. `onCreated` ahora se llama SIEMPRE
          // (éxito o error), así que esto nunca se queda esperando para siempre.
          if (this.$_resolveAiGoalChainSaved) {
            this.$_resolveAiGoalChainSaved();
            this.$_resolveAiGoalChainSaved = null;
          }
        });
      } else {
        this.$emit('create-goal', goal);
      }

      this.resetState();
    },
    // reenvía 'add-objetive' (burbujeado desde SubAreaContainer.vue) hacia
    // AddProgramService.vue -- método con nombre (no una expresión inline con $event) por el
    // mismo motivo que relayCreateGoal de abajo: un `$emit('add-objetive', $event)` inline solo
    // reenvía el primer argumento, y ahora hace falta también el segundo (`onCreated`, la señal
    // de que el guardado real terminó -- ver AddProgramService.handleAddObjetive), que usa
    // "Objetivo con Asistente IA" (SubAreaGoalCallout.vue) para apagar el loading de su modal.
    relayAddObjetive(objetive, onCreated) {
      this.$emit('add-objetive', objetive, onCreated);
    },
    // Reenvía 'add-sub-area-goal' (burbujeado desde SubAreaNecessity.vue a través de
    // SubAreaContainer.vue) como 'create-goal' hacia AddProgramService.vue -- referencia de
    // método (no una expresión inline con $event) para que Vue reenvíe TODOS los argumentos,
    // incluido el callback `onCreated` del encadenado (un $emit('...', $event) inline solo
    // reenvía el primer argumento).
    relayCreateGoal(goal, onCreated) {
      this.$emit('create-goal', goal, onCreated);
    },
    // Meta creada vía "Meta con Asistente IA" DENTRO de una Sub Área/Necesidad ya existente --
    // mismo `pendingAiChainGoalId` que usa el disparador de arriba, porque ambos se pasan al
    // mismo prop hacia todos los SubAreaContainer, sin importar cuál de los dos disparó la
    // creación. `goalId` ya viene resuelto (SubAreaNecessity.saveGoalHandler lo recibe de su
    // propio callback `onCreated`).
    handleAiChainGoalCreated(goalId) {
      this.pendingAiChainGoalId = goalId;
    },
    // --- AG-05 -- "Meta con Asistente IA" (primera meta / Sub Área nueva). A diferencia del
    // mismo disparador en SubAreaNecessity.vue, acá Sub Área y Fortaleza/Necesidad las elige
    // el especialista en el GoalForm de abajo (showClassifier). El modal de IA nunca se abre
    // de una vez -- siempre queda pendiente (pendingAiGoalTrigger) hasta que el especialista
    // confirme la clasificación con "Continuar" dentro del GoalForm (paso 1 de 2,
    // showClassifier) -- ver handleClassifierConfirmed. ---
    handleAiGoalTriggerClick(event) {
      this.forceShow = true;
      this.formOpenedViaAi = true;

      // Siempre queda pendiente hasta que el especialista confirme el paso 1 con
      // "Continuar" -- incluso si Sub Área/Fortalezas ya venían completas de un alta manual
      // en este mismo panel (el watcher de twoStepGate en GoalForm.vue reinicia
      // classifierConfirmed a false apenas formOpenedViaAi pasa a true). Abrir el modal acá
      // directo en ese caso alcanzaba a mostrarse a la vez que el paso 1 se reiniciaba --
      // dos UI compitiendo. Así el flujo es siempre el mismo sin importar cómo se llegó.
      // se guarda el rect del click original porque para cuando el especialista confirme
      // la clasificación (handleClassifierConfirmed) ya no está apuntando al botón.
      this.pendingAiGoalTrigger = true;
      const rect = event?.currentTarget?.getBoundingClientRect();
      this.pendingAiGoalAnchorRect = rect
        ? { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width, height: rect.height }
        : null;
    },
    // GoalForm emite 'classifier-confirmed' al apretar "Continuar" en el paso 1
    // (Sub Área + Fortaleza/Necesidad) -- si ese "Continuar" viene de un disparo de IA
    // pendiente, recién ahí se convoca al agente; si viene del alta manual normal
    // ("Añadir Meta"), pendingAiGoalTrigger ya está en false y no hace nada (el propio
    // GoalForm revela el resto de los campos por su cuenta).
    handleClassifierConfirmed() {
      if (!this.pendingAiGoalTrigger) return;

      this.pendingAiGoalTrigger = false;
      this.openAiGoalModal();
    },
    openAiGoalModal(event) {
      const rect = event?.currentTarget?.getBoundingClientRect();
      this.aiGoalModal.anchorRect = rect
        ? { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width, height: rect.height }
        : this.pendingAiGoalAnchorRect;
      this.pendingAiGoalAnchorRect = null;
      this.aiGoalModal.visible = true;
      this.requestAiGoal();
    },
    closeAiGoalModal() {
      this.aiGoalModal.visible = false;
    },
    async requestAiGoal() {
      this.aiGoalModal.loading = true;
      // por si quedó en 'Guardando la meta seleccionada…' de un "Usar esta meta"
      // previo (ver handleAiGoalSelected) -- Regenerar/abrir de nuevo siempre debe mostrar el
      // texto de generación, no el de guardado.
      this.aiGoalModal.loadingText = 'Generando metas sugeridas…';
      this.aiGoalModal.error = '';
      try {
        const { data } = await generateSuggestion({
          module: 'PEI',
          sectionArea: 'V-Metas',
          peiId: store.getters.SelectedPei,
          subAreaId: this.newGoal.subArea?.id,
          // El backend distingue Fortaleza de Necesidad por columna (NecessityId/StrengthId en
          // GoalNecessityStrength), no por el id solo -- un id de Fortaleza y uno de Necesidad
          // pueden coincidir numéricamente (son tablas distintas). Se manda el objeto completo
          // {id, isStrength}, igual que ya se hace al guardar la Meta, en vez de una lista de
          // ids sueltos.
          strenghtsAndNecessities: this.newGoal.strenghtsAndNecessities || [],
        });

        this.applyAiGoalResult(data);
      } catch (err) {
        this.aiGoalModal.error =
          err?.response?.data ||
          'No se pudieron generar metas sugeridas. Intente nuevamente.';
      } finally {
        this.aiGoalModal.loading = false;
      }
    },
    async handleRefineGoal(instruccion) {
      if (!this.aiGoalModal.aiGenerationLogId) return;
      this.aiGoalModal.loading = true;
      this.aiGoalModal.error = '';
      try {
        const { data } = await refineSuggestion({
          aiGenerationLogId: this.aiGoalModal.aiGenerationLogId,
          teacherInstruction: instruccion,
        });

        this.applyAiGoalResult(data);
      } catch (err) {
        this.aiGoalModal.error =
          err?.response?.data ||
          'No se pudo refinar la sugerencia. Intente nuevamente.';
      } finally {
        this.aiGoalModal.loading = false;
      }
    },
    applyAiGoalResult(data) {
      this.aiGoalModal.aiGenerationLogId = data.aiGenerationLogId;
      this.aiGoalModal.feedback = null;
      this.aiGoalModal.feedbackSubmitting = false;
      this.aiGoalModal.bestPractices =
        data.bestPractices || this.aiGoalModal.bestPractices || null;

      if (data.isBlocked) {
        this.aiGoalModal.error = data.blockedReason;
        this.aiGoalModal.suggestions = [];
        return;
      }

      let parsed = {};
      try {
        parsed = JSON.parse(data.suggestion);
      } catch (e) {
        parsed = {};
      }

      // el modelo puede devolver "actividad" (la única parte de texto libre de la Meta)
      // partida en párrafos -- se aplana a texto corrido acá, ANTES de guardarla en la
      // sugerencia (ver aiFlattenSuggestionText en aiSuggestionEditMixin.js), porque
      // applyAiGoalSuggestion usa suggestion.actividad DIRECTO cuando no hubo edición.
      this.aiGoalModal.suggestions = (parsed.metas || []).map((m) => (
        { ...m, actividad: this.aiFlattenSuggestionText(m.actividad), _selected: false }
      ));
    },
    // "editar antes de usar": la parte libre de editar -- SOLO Actividad. Ni Término ni
    // Adecuacidad se editan como texto libre (ver el comentario del template) -- se eligen
    // aparte con los dos dropdowns de catálogo.
    formatAiGoalEditableText(suggestion) {
      return suggestion.actividad || '';
    },
    // Vista completa de solo lectura (Término, Persona, Actividad, Adecuacidad) -- ya no se
    // usa para lo que se edita a mano (ver formatAiGoalEditableText).
    formatAiGoalPreview(suggestion) {
      const studentName = store.getters.SelectedStudent.firstName;
      return `${suggestion.termino}, ${studentName} ${suggestion.actividad}. ${suggestion.adecuacidad}.`;
    },
    // Término a mostrar/usar: el elegido a mano en el dropdown de edición
    // (suggestion._aiEditTerm) si hay uno, si no el de catálogo que ya trajo la IA.
    aiGoalDisplayTermino(suggestion) {
      return (suggestion._aiEditTerm && suggestion._aiEditTerm.name) || suggestion.termino;
    },
    // Adecuacidad a mostrar/usar: la elegida a mano en el dropdown de edición
    // (suggestion._aiEditSuitability) si hay una, si no la de catálogo que ya trajo la IA.
    aiGoalDisplayAdecuacidad(suggestion) {
      return (suggestion._aiEditSuitability && suggestion._aiEditSuitability.name) || suggestion.adecuacidad;
    },
    // Envoltorio de aiEditToggle (aiSuggestionEditMixin.js) que además sincroniza los
    // dropdowns de Término/Adecuacidad -- son propios de Metas (no viven en el mixin
    // compartido con Objetivos/Tab III-A/III-B, que no tienen este mismo riesgo, ver
    // applyAiGoalSuggestion). Al entrar a editar, precarga los mismos valores que ya matcheó la
    // IA; al cancelar, los descarta junto con el texto.
    handleAiGoalEditToggle(suggestion) {
      const wasActive = this.aiEditIsActive(suggestion);
      this.aiEditToggle(suggestion, this.formatAiGoalEditableText);

      if (!wasActive) {
        const { matchedTerm, matchedSuitability } = this.matchAiGoalSuggestion(suggestion);
        this.$set(suggestion, '_aiEditTerm', matchedTerm);
        this.$set(suggestion, '_aiEditSuitability', matchedSuitability);
      } else {
        this.$set(suggestion, '_aiEditTerm', null);
        this.$set(suggestion, '_aiEditSuitability', null);
      }
    },
    // extraído de handleAiGoalSelected para reusarlo también en la inserción masiva
    // (aiBulkAddSelected, aiBulkSelectMixin.js) sin duplicar el criterio de match.
    matchAiGoalSuggestion(suggestion) {
      const normalize = (text) => (text || '').trim().toLowerCase();

      const matchedTerm = this.termOptions.find(
        (x) => normalize(x.name) === normalize(suggestion.termino)
      ) || null;
      const matchedSuitability = this.suitabilityOptions.find(
        (x) => normalize(x.name) === normalize(suggestion.adecuacidad)
      ) || null;

      return { matchedTerm, matchedSuitability };
    },
    // Una sugerencia es "completa" (auto-guardable, sin abrir el form para completarla a mano)
    // cuando Término y Adecuacidad matchean el catálogo real y trae Actividad -- mismo criterio
    // que ya usaba handleAiGoalSelected. Solo las completas son elegibles para la casilla de
    // selección masiva (ver aiBulkIsSelectable, abajo, y aiBulkSelectMixin.js).
    isAiGoalSuggestionComplete(suggestion) {
      const { matchedTerm, matchedSuitability } = this.matchAiGoalSuggestion(suggestion);
      return !!(matchedTerm && matchedSuitability && suggestion.actividad);
    },
    // Adaptador de aiBulkSelectMixin.js -- ver el contrato documentado ahí.
    aiBulkIsSelectable(suggestion) {
      return this.isAiGoalSuggestionComplete(suggestion);
    },
    // lógica de guardado de UNA sugerencia, extraída de handleAiGoalSelected para
    // reusarla también en la inserción masiva. Reutiliza el MISMO GoalForm/newGoal de "Añadir
    // Meta" (igual que antes) -- funciona en cadena porque cada llamada espera a que el
    // guardado anterior termine del todo (incl. resetState()) antes de que la siguiente
    // sobreescriba newGoal. Devuelve true si de verdad se guardó (sugerencia completa), false
    // si solo se dejó prellenado el form para completar a mano (incompleta).
    async applyAiGoalSuggestion(suggestion) {
      const { matchedTerm, matchedSuitability } = this.matchAiGoalSuggestion(suggestion);
      // "editar antes de usar" (ver aiSuggestionEditMixin.js): si el especialista
      // corrigió a mano el texto de la tarjeta, ESE texto reemplaza la descripción final (ver
      // GoalForm.vue -> descriptionOverride, que se salta BuildGoalDescription) y la Meta queda
      // SIN vincular a la corrida de IA para la auditoría (ver
      // PeiEngine.AddAcademicAreaGoalAsync/RegisterAppliedAiField) -- si no se editó, todo sigue
      // exactamente igual que antes.
      //
      // Ni Término ni Adecuacidad salen de texto libre: si el especialista los cambió, vienen
      // de los dropdowns de edición (suggestion._aiEditTerm/_aiEditSuitability, ver
      // handleAiGoalEditToggle), que solo ofrecen valores reales de catálogo -- nunca del
      // textarea. finalTerm/finalSuitability son lo que de verdad se guarda en la Meta, y sus
      // .name son lo mismo que se pega en la descripción final -- siempre salen de la MISMA
      // fuente, así que no pueden desincronizarse aunque el especialista elija valores
      // distintos a los de la IA.
      const wasEdited = this.aiEditHasEditedText(suggestion);
      const finalTerm = (wasEdited && suggestion._aiEditTerm) || matchedTerm;
      const finalSuitability = (wasEdited && suggestion._aiEditSuitability) || matchedSuitability;
      const finalTermText = finalTerm ? finalTerm.name : suggestion.termino;
      const finalAdecuacidadText = finalSuitability ? finalSuitability.name : suggestion.adecuacidad;
      const studentName = store.getters.SelectedStudent.firstName;
      const finalActivityText = wasEdited
        ? this.aiEditFinalText(suggestion, this.formatAiGoalEditableText)
        : (suggestion.actividad || '');

      this.newGoal = {
        ...this.newGoal,
        term: finalTerm,
        activity: finalActivityText,
        suitability: finalSuitability,
        studentName,
        descriptionOverride: wasEdited
          ? `${finalTermText}, ${studentName} ${finalActivityText}. ${finalAdecuacidadText}.`
          : null,
        descriptionAiGenerationLogId: wasEdited ? null : this.aiGoalModal.aiGenerationLogId,
      };

      if (this.aiGoalModal.aiGenerationLogId) {
        markSuggestionUsed({ aiGenerationLogId: this.aiGoalModal.aiGenerationLogId }).catch(() => {});
      }

      if (matchedTerm && matchedSuitability && suggestion.actividad) {
        // antes esto cerraba el modal de inmediato (`aiGoalModal.visible = false`)
        // y el guardado real (POST a AddProgramServiceGoal + refetch de goals, ver
        // AddProgramService.handleCreateGoal) corría totalmente invisible -- el especialista
        // no veía ningún indicador durante ese tiempo. Ahora dejamos el modal abierto EN
        // LOADING (mismo spinner que ya se usa al generar/refinar sugerencias) hasta que el
        // guardado de verdad termine, y recién ahí lo cerramos.
        this.pendingAiGoalChain = true;
        this.aiGoalModal.loading = true;
        // En bulk, el loadingText por-item ("Guardando meta X de N…") ya lo puso
        // aiBulkAddSelected (aiBulkSelectMixin.js) -- no lo pisamos acá.
        if (!this.aiBulkSelectInProgress) {
          this.aiGoalModal.loadingText = 'Guardando la meta seleccionada…';
        }
        // resetState() (al final de saveGoalHandler, tras CADA guardado) deja
        // forceShow en false, y showNewEmptyGoal también puede volverse false apenas
        // this.goals ya no está vacío (tras el 1er guardado del bulk) -- eso desmonta
        // <GoalForm ref="newEmptyGoalForm"> (v-if="showNewEmptyGoal"). Sin esto, la 2ª
        // sugerencia del bulk en adelante fallaría al no encontrar la ref. Se reafirma acá,
        // en cada llamada, en vez de solo en el click del disparador -- mismo motivo para
        // formOpenedViaAi (el fondo violeta debe seguir activo durante todo el bulk).
        this.forceShow = true;
        this.formOpenedViaAi = true;

        // saveGoalHandler() resuelve esto vía el callback `onCreated` que ya recibe de
        // AddProgramService.handleCreateGoal -- esa es la señal real de que terminó (éxito O
        // error; ver el comentario en handleCreateGoal), no una espera arbitraria.
        const savedPromise = new Promise((resolve) => {
          this.$_resolveAiGoalChainSaved = resolve;
        });

        await this.$nextTick();
        await this.$refs.newEmptyGoalForm.save();
        await savedPromise;

        if (!this.aiBulkSelectInProgress) {
          this.aiGoalModal.loading = false;
        }

        return true;
      }

      return false;
    },
    async handleAiGoalSelected(suggestion) {
      await this.applyAiGoalSuggestion(suggestion);
      this.aiGoalModal.visible = false;
    },
    // Adaptador de aiBulkSelectMixin.js -- ver el contrato documentado ahí.
    async aiBulkSaveOne(suggestion) {
      return await this.applyAiGoalSuggestion(suggestion);
    },
    aiBulkItemLabel(count) {
      return count === 1 ? 'meta sugerida' : 'metas sugeridas';
    },
    // resetState() (que corre al final de saveGoalHandler, después de CADA guardado,
    // incl. a mitad del bulk) deja newGoal.subArea/strenghtsAndNecessities en null/[] otra vez
    // -- a diferencia de SubAreaNecessity.vue (que los saca de this.value.necessityGoals[0],
    // estable), acá SÍ son la fuente real que se guarda. Sin esto, la 2ª sugerencia en adelante
    // del guardado masivo se guardaría sin Sub Área/Necesidad. aiBulkBeforeAll/aiBulkBeforeEach
    // son los hooks opcionales de aiBulkSelectMixin.js -- SubAreaNecessity.vue y
    // SubAreaGoalCallout.vue no los necesitan y no los definen.
    aiBulkBeforeAll() {
      this.$_aiBulkCapturedSubArea = this.newGoal.subArea;
      this.$_aiBulkCapturedNecessities = this.newGoal.strenghtsAndNecessities;
    },
    aiBulkBeforeEach() {
      this.newGoal.subArea = this.$_aiBulkCapturedSubArea;
      this.newGoal.strenghtsAndNecessities = this.$_aiBulkCapturedNecessities;
    },
    async handleFeedbackGoal(liked) {
      const logId = this.aiGoalModal.aiGenerationLogId;
      if (!logId || this.aiGoalModal.feedbackSubmitting) return;

      const previous = this.aiGoalModal.feedback;
      this.aiGoalModal.feedback = liked ? 'up' : 'down';
      this.aiGoalModal.feedbackSubmitting = true;

      try {
        await submitFeedback({ aiGenerationLogId: logId, liked });
      } catch (err) {
        this.aiGoalModal.feedback = previous;
        this.ShowToast('', 'No se pudo registrar tu respuesta. Intente nuevamente.', 'error');
      } finally {
        this.aiGoalModal.feedbackSubmitting = false;
      }
    },
  },
  watch: {
    havePendingChanges(newVal) {
      this.$emit('pending-changes', newVal);
    },
  }
};
</script>

<style scoped>
.goal-top-actions__container {
  row-gap: 8px;
}

/* fondo/borde violeta claro (mismo esquema que .ai-field-highlighted en
   CustomTextArea.vue y el resto del Asistente de IA -- ver Tab3.vue) para distinguir a
   simple vista en cuál Meta se está trabajando mientras el formulario de alta está abierto,
   sea por "Añadir Meta" o por "Meta con Asistente IA". Mismo tratamiento aplicado también en
   SubAreaNecessity.vue (.add-subarea-necessity__container), a nivel de cada Sub Área. */
.goal-new-form__container {
  background-color: #faf8ff;
  border: 1.5px solid #ba65ff;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* degradado violeta, tomado 1:1 del nodo "Bot Icon" del mockup de Figma (fileKey
   2Gbxwn31JzazuECLYZyoeS, nodeId 6511:24996). Ver el mismo cambio en SubAreaGoalCallout.vue
   (.ai-objective-trigger) y SubAreaNecessity.vue.

   Versión compacta (Variante B del mockup HTML aprobado por el usuario, radio 4px -- igual
   al resto de los botones cuadrados de la pantalla, a diferencia de la Variante A redonda que
   no se eligió): en reposo el botón mide 36px (solo ícono, max-width recortado con
   overflow:hidden); en :hover/:focus-visible max-width crece a 260px y el degradado se oscurece,
   dejando ver el texto (.ai-trigger-label), que además hace fade-in con un pequeño delay para
   que se sienta después de que el botón ya empezó a abrirse. Se anima max-width en vez de width
   porque width no admite una transición fluida cuando el contenido es "auto". focus-visible
   además de hover para que sea accesible por teclado. */
.ai-goal-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 9px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(114.12deg, #5c58ed 24.303%, #ba65ff 134.64%);
  color: #fff;
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 1.4;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  max-width: 36px;
  transition: max-width 0.28s cubic-bezier(0.4, 0, 0.2, 1),
              background 0.15s ease-in-out;
}

.ai-goal-trigger:hover,
.ai-goal-trigger:focus-visible {
  max-width: 260px;
  background: linear-gradient(114.12deg, #4a45d6 24.303%, #a94dff 134.64%);
}

.ai-goal-trigger .ai-trigger-icon {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-goal-trigger .ai-trigger-label {
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.18s ease 0.05s, transform 0.18s ease 0.05s;
}

.ai-goal-trigger:hover .ai-trigger-label,
.ai-goal-trigger:focus-visible .ai-trigger-label {
  opacity: 1;
  transform: translateX(0);
}

.ai-modal-empty {
  color: #11291b;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
}

/* tarjeta en columna: primero el header (pastilla "Meta #N" sola, da contexto), después
   el contenido (checkbox + texto + Término/Adecuacidad + botón) -- ver .ai-goal-suggestion__header
   y .ai-goal-suggestion__content más abajo. */
.ai-goal-suggestion {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid #c1c0f8;
  border-radius: 4px;
  padding: 14px 16px;
  background-color: #f8f5ff;
}

/* contenido de la tarjeta (checkbox + texto + Término/Adecuacidad + botón), DEBAJO de
   la pastilla. Es un bloque normal (no flex): el checkbox flota (float: left, ver
   .ai-goal-suggestion__checkbox) y el texto lo rodea -- la 1ª línea del párrafo empieza al lado
   del checkbox, las líneas siguientes (y Término/Adecuacidad/botón, que llevan clear: both) caen
   a todo el ancho de la tarjeta. Reemplaza a .ai-goal-suggestion__body (columna flex) de la ronda
   anterior.

   El clearfix del checkbox flotado NO puede ser overflow: hidden (como estaba antes): eso
   recortaba en seco (sin scroll posible) la lista de opciones de los dropdowns de
   Término/Adecuacidad apenas crecía más que el contenido normal de la tarjeta -- bug real
   reportado por el usuario ("no logro ver las demás opciones, el scroll está bloqueado"). Un
   ::after con clear: both consigue el mismo efecto (la tarjeta crece con la altura del
   checkbox flotado) sin recortar nada que sobresalga. */
.ai-goal-suggestion__content::after {
  content: '';
  display: table;
  clear: both;
}

/* contexto ("Sub Área: X   Necesidad: Y") arriba de la lista de Metas sugeridas --
   texto plano, sin caja, mismo criterio liviano que el contexto del modal de Objetivos (ver
   SubAreaGoalCallout.vue): es referencia, no debe competir visualmente con las tarjetas de
   sugerencias de abajo. */
.ai-goal-context__breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #4a5a53;
  margin: 0 0 4px 0;
}

.ai-goal-context__breadcrumb strong {
  color: #11291b;
}

/* fila de encabezado de cada tarjeta sugerida: la pastilla "Meta #N". */
/* el checkbox se sacó de este header -- ahora la pastilla queda sola arriba, para dar
   contexto antes del checkbox (ver .ai-goal-suggestion__content). */
.ai-goal-suggestion__header {
  display: flex;
  align-items: center;
  /* justify-content: space-between para que el botón ícono de Editar (ver
     .ai-btn-icon, agregado -- mockup aprobado para descargar la fila de acciones de
     abajo a un solo botón) quede del lado opuesto a la pastilla/etiqueta "Editado". */
  justify-content: space-between;
}

/* agrupa pastilla "Meta #N" + etiqueta "Editado" (si aplica) del lado izquierdo del
   header, para que el botón ícono de Editar quede solo del lado derecho vía justify-content:
   space-between del padre, sin importar cuántos elementos haya del lado izquierdo. */
.ai-goal-suggestion__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* "editar antes de usar" (ver aiSuggestionEditMixin.js): señal SOLO visual de que el
   texto ya no es el que propuso la IA tal cual. Gris neutro a propósito (no verde/violeta como
   las acciones "positivas" Usar/Regenerar) -- es informativo, no un estado de éxito/error. */
.ai-goal-suggestion__edited-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 4px;
  background: #eef0ef;
  color: #57685f;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* CustomCheckbox (@/components/common) -- mismo componente de checkbox que ya usa el resto del
   proyecto (ver ResultRow.vue, etc.) en vez del <input type="checkbox"> plano que tenía esto
   antes. El label que arma internamente CustomCheckbox vive en SU propio scope -- se estiliza
   acá con ::v-deep para que el texto "Seleccionar" siga el mismo tratamiento tipográfico que el
   resto de este modal. */
/* CustomCheckbox usa "input-group" como clase raíz (de Bootstrap, global), que trae
   width: 100% por defecto -- si no se anula, el checkbox flotado (float: left) se estira al
   ancho completo del contenedor en vez de medir solo su contenido. width: auto lo anula. El
   checkbox ahora flota a la izquierda del párrafo de texto (ver .ai-goal-suggestion__content) --
   la 1ª línea del texto queda al lado del checkbox, las líneas siguientes caen a todo el ancho
   una vez que el texto supera su altura. */
.ai-goal-suggestion__checkbox {
  float: left;
  width: auto;
  margin: 2px 10px 6px 0;
}

.ai-goal-suggestion__checkbox ::v-deep label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #4a5a53;
  cursor: pointer;
}

/* Pastilla "Meta #N" DENTRO de cada tarjeta de sugerencia ya existente (numera cuál es cuál
   cuando hay más de una) -- chica y a propósito, para no competir con el botón "Usar esta
   meta" de la misma tarjeta. */
/* radio 4px (no píldora/50px) para que quede rectangular, igual que los botones
   ".ai-btn" (ej. "Usar esta meta") -- pedido explícito del usuario. */
.ai-goal-suggestion__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 6px 14px;
  border-radius: 4px;
  background: linear-gradient(104deg, #5c58ed 24.303%, #ba65ff 134.64%);
  color: #fff;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 13px;
}

.ai-goal-suggestion__text {
  margin: 0;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #11291b;
  white-space: pre-wrap;
}

/* "editar antes de usar": reemplaza .ai-goal-suggestion__text mientras
   aiEditIsActive(suggestion) es true (ver template). Mismo foco violeta que ya usa "Refinar
   Resultado" (ver AiAssistantModal.vue, .ai-modal-refine-input:focus) para que se sienta parte
   del mismo lenguaje visual del asistente, no un input nuevo suelto. width: 100% + display:
   block para que ocupe el mismo ancho que el texto normal a pesar del checkbox flotado al lado. */
.ai-goal-suggestion__text-edit {
  display: block;
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #a062fb;
  border-radius: 4px;
  outline: none;
  box-shadow: 0 0 0 3px rgba(92, 88, 237, 0.15);
  background: #fff;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #11291b;
  resize: vertical;
}

/* Dropdowns de Término/Adecuacidad, arriba y debajo del textarea mientras se edita -- ver
   el comentario del template (handleAiGoalEditToggle). Ancho acotado (no 100% como el
   textarea) para que se lean como campos de selección, no como continuación del texto libre. */
.ai-goal-suggestion__catalog-edit {
  max-width: 320px;
  margin: 8px 0;
}

/* clear: both asegura que Término/Adecuacidad quede SIEMPRE debajo del checkbox
   flotado, sin importar cuántas líneas ocupe el texto de arriba. */
.ai-goal-suggestion__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  margin-top: 10px;
  clear: both;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #4a5a53;
}

.ai-goal-suggestion__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.ai-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.ai-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 4px;
  padding: 7px 16px;
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.ai-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ai-btn-outline {
  background: #fff;
  border: 1px solid #2db782;
  color: #2db782;
}

/* botón ícono de Editar/Cancelar (ver aiSuggestionEditMixin.js, aiEditToggle):
   reemplaza al par de botones de texto ".ai-btn-ghost" que vivía en
   .ai-goal-suggestion__actions -- mockup aprobado por el usuario para dejar una sola fila de
   acciones (el botón principal "Usar esta meta") en cada tarjeta. Sin fondo ni borde en reposo
   (pedido del usuario -- se veía "muy cuadrado"): solo el ícono, gris neutro a propósito, igual
   criterio que .ai-goal-suggestion__edited-tag -- no es una acción "positiva" tipo Usar
   (violeta), es un modo de vista. El fondo aparece recién en :hover/--active, como feedback de
   que el botón es interactivo/está activo, no como marco permanente. */
.ai-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  background: transparent;
  border: none;
  color: #57685f;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;
  /* tamaño del ícono FontAwesome (fa-pencil/fa-times) que va adentro -- antes lo daba el
     prop :size del componente SVG propio, ver el comentario del template. */
  font-size: 14px;
}

.ai-btn-icon:hover {
  background: #eef0ef;
}

/* Mientras se está editando (botón mostrando la "X" de Cancelar) -- mismo fondo que
   .ai-goal-suggestion__edited-tag para que se lea como "modo activo", no como error. */
.ai-btn-icon--active {
  background: #eef0ef;
  color: #11291b;
}

.ai-btn-primary {
  background: linear-gradient(104deg, #5c58ed 24.303%, #ba65ff 134.64%);
  border: 1px solid #5c58ed;
  color: #fff;
}

.ai-btn-primary:hover:not(:disabled) {
  background: linear-gradient(104deg, #4a45d6 24.303%, #a94dff 134.64%);
}
</style>
